import React, { useEffect, useState } from "react";
import useCookieConsent, { Consent } from "@/hooks/useCookieConsent";

export function CookieConsent() {
  const { consent, save, acceptAll, rejectNonEssential, defaultConsent } = useCookieConsent();
  const [open, setOpen] = useState(false);
  const [localPrefs, setLocalPrefs] = useState<Consent | null>(null);

  useEffect(() => {
    if (consent === null) setOpen(true);
    else setOpen(false);
  }, [consent]);

  useEffect(() => {
    if (consent) setLocalPrefs(consent);
    else setLocalPrefs(defaultConsent());
  }, [consent, defaultConsent]);

  const toggle = (key: "analytics" | "marketing") => {
    if (!localPrefs) return;
    setLocalPrefs({ ...localPrefs, [key]: !localPrefs[key] });
  };

  if (!open || !localPrefs) return null;

  return (
    <div
      aria-live="polite"
      className="fixed left-4 bottom-4 z-50 bg-black text-white border border-border rounded-xl p-4 shadow-lg w-80 md:w-96"
      role="dialog"
      aria-label="Cookie preferences"
    >
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold mb-1">We use cookies</h3>
          <p className="text-sm text-gray-300 mb-3">
            Necessary cookies are required. Choose optional cookies to enable analytics and marketing.
          </p>

          <div className="space-y-2">
            <label className="flex items-center gap-3 text-sm">
              <input type="checkbox" checked readOnly className="accent-primary" />
              <span>Necessary (required)</span>
            </label>

            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={!!localPrefs.analytics}
                onChange={() => toggle("analytics")}
                className="accent-primary"
              />
              <span>Analytics</span>
            </label>

            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={!!localPrefs.marketing}
                onChange={() => toggle("marketing")}
                className="accent-primary"
              />
              <span>Marketing / Third‑party embeds</span>
            </label>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <button
            onClick={() => {
              if (localPrefs) save(localPrefs);
              setOpen(false);
            }}
            className="btn btn-primary px-3 py-2 text-sm"
          >
            Save preferences
          </button>

          <button
            onClick={() => {
              acceptAll();
              setOpen(false);
            }}
            className="btn btn-outline px-3 py-2 text-sm"
          >
            Accept all
          </button>

          <button
            onClick={() => {
              rejectNonEssential();
              setOpen(false);
            }}
            className="text-xs text-muted-foreground underline"
          >
            Reject non‑essential
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
